/**
 * Route Section
 * Origin/Destination Ports (autocomplete), Transshipment Ports, ETA, Shipping Company
 */

import { useState } from 'react';
import { Input } from '@/components/base/input/input';
import { Button } from '@/components/base/buttons/button';
import { DatePicker } from '@/components/application/date-picker/date-picker';
import { XCircle, Plus } from '@untitledui/icons';
import type { DateValue } from 'react-aria-components';
import type { ContainerFormData, SelectOption } from '@/hooks/useContainerForm';
import { useTranslation } from 'react-i18next';

interface RouteSectionProps {
    formData: ContainerFormData;
    onInputChange: (field: keyof ContainerFormData, value: string) => void;
    onPortSelect: (type: 'origin' | 'destination', portId: string) => void;
    originPortSearch: string;
    setOriginPortSearch: (value: string) => void;
    destPortSearch: string;
    setDestPortSearch: (value: string) => void;
    originPortOptions: SelectOption[];
    destPortOptions: SelectOption[];
    carrierOptions: SelectOption[];
    carrierSearch: string;
    setCarrierSearch: (value: string) => void;
    etaDate: DateValue | null;
    setEtaDate: (date: DateValue | null) => void;
    onAddCarrier: () => void;
    // Transshipment ports
    transshipmentPorts: Array<{ portId: string; portDisplay: string }>;
    transshipmentPortSearches: string[];
    setTransshipmentPortSearch: (index: number, value: string) => void;
    transshipmentPortOptions: SelectOption[][];
    onAddTransshipmentPort: () => void;
    onRemoveTransshipmentPort: (index: number) => void;
    onTransshipmentPortSelect: (index: number, portId: string) => void;
    isEditMode?: boolean;
}

export function RouteSection({
    formData,
    onInputChange,
    onPortSelect,
    originPortSearch,
    setOriginPortSearch,
    destPortSearch,
    setDestPortSearch,
    originPortOptions,
    destPortOptions,
    carrierOptions,
    carrierSearch,
    setCarrierSearch,
    etaDate,
    setEtaDate,
    onAddCarrier,
    transshipmentPorts,
    transshipmentPortSearches,
    setTransshipmentPortSearch,
    transshipmentPortOptions,
    onAddTransshipmentPort,
    onRemoveTransshipmentPort,
    onTransshipmentPortSelect,
    isEditMode = false,
}: RouteSectionProps) {
    const { t } = useTranslation('pages:containers');
    const [isCarrierInputFocused, setIsCarrierInputFocused] = useState(false);

    // Initialize carrierSearch with carrierName if we have a name but no ID (edit mode with unmatched carrier)
    const effectiveCarrierSearch = carrierSearch || (formData.carrierName && !formData.carrierCompanyId ? formData.carrierName : '');

    // Filter carriers based on search (client-side)
    const filteredCarrierOptions = effectiveCarrierSearch.trim()
        ? carrierOptions.filter(carrier => {
            const searchLower = effectiveCarrierSearch.toLowerCase().trim();
            return carrier.label.toLowerCase().includes(searchLower) ||
                carrier.id.toLowerCase().includes(searchLower);
        })
        : carrierOptions;

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.route')}</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Origin Port Autocomplete */}
                <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                        {t('form.fields.originPort', 'Origin Port')}
                    </label>
                    {formData.originPortId ? (
                        <div className="flex items-center gap-2">
                            <div className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                                {formData.originPortDisplay || (isEditMode ? 'Loading...' : '')}
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                color="secondary"
                                onClick={() => {
                                    onInputChange('originPortId', '');
                                    onInputChange('originPortDisplay', '');
                                    setOriginPortSearch('');
                                }}
                            >
                                <XCircle className="size-4" />
                            </Button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Input
                                placeholder={t('form.fields.originPortPlaceholder', 'Search port...')}
                                value={originPortSearch}
                                onChange={setOriginPortSearch}
                                size="md"
                                aria-label={t('form.fields.originPort', 'Origin Port')}
                            />
                            {originPortOptions.length > 0 && originPortSearch.length >= 2 && (
                                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                                    {originPortOptions.map(port => (
                                        <button
                                            key={port.id}
                                            type="button"
                                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary"
                                            onClick={() => onPortSelect('origin', port.id)}
                                        >
                                            {port.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Destination Port Autocomplete */}
                <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                        {t('form.fields.destinationPort', 'Destination Port')}
                    </label>
                    {formData.destinationPortId ? (
                        <div className="flex items-center gap-2">
                            <div className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                                {formData.destinationPortDisplay || (isEditMode ? 'Loading...' : '')}
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                color="secondary"
                                onClick={() => {
                                    onInputChange('destinationPortId', '');
                                    onInputChange('destinationPortDisplay', '');
                                    setDestPortSearch('');
                                }}
                            >
                                <XCircle className="size-4" />
                            </Button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Input
                                placeholder={t('form.fields.destinationPortPlaceholder', 'Search port...')}
                                value={destPortSearch}
                                onChange={setDestPortSearch}
                                size="md"
                                aria-label={t('form.fields.destinationPort', 'Destination Port')}
                            />
                            {destPortOptions.length > 0 && destPortSearch.length >= 2 && (
                                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                                    {destPortOptions.map(port => (
                                        <button
                                            key={port.id}
                                            type="button"
                                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary"
                                            onClick={() => onPortSelect('destination', port.id)}
                                        >
                                            {port.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Transshipment Ports */}
            <div className="mt-6">
                <div className="flex items-center justify-between gap-4 mb-2">
                    <label className="block text-sm font-medium text-primary">
                        {t('form.fields.transshipmentPorts', 'Transshipment Ports')}
                        <span className="ml-1 text-xs font-normal text-tertiary">
                            {t('form.fields.optional', '(optional)')}
                        </span>
                    </label>
                    <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        iconLeading={Plus}
                        onClick={onAddTransshipmentPort}
                    >
                        {t('form.addPort', 'Add Port')}
                    </Button>
                </div>
                {transshipmentPorts.length > 0 && (
                    <div className="space-y-2">
                        {transshipmentPorts.map((tp, index) => (
                            <div key={index} className="flex items-center gap-2">
                                {tp.portId ? (
                                    <>
                                        <div className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                                            {tp.portDisplay}
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            color="secondary"
                                            onClick={() => onRemoveTransshipmentPort(index)}
                                        >
                                            <XCircle className="size-4" />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="relative flex-1">
                                            <Input
                                                placeholder={t('form.fields.transshipmentPortPlaceholder', 'Search port...')}
                                                value={transshipmentPortSearches[index] || ''}
                                                onChange={(value) => setTransshipmentPortSearch(index, value)}
                                                size="md"
                                                aria-label={t('form.fields.transshipmentPort', 'Transshipment Port')}
                                            />
                                            {(transshipmentPortOptions[index]?.length ?? 0) > 0 &&
                                             (transshipmentPortSearches[index]?.length ?? 0) >= 2 && (
                                                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                                                    {transshipmentPortOptions[index].map(port => (
                                                        <button
                                                            key={port.id}
                                                            type="button"
                                                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary"
                                                            onClick={() => onTransshipmentPortSelect(index, port.id)}
                                                        >
                                                            {port.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            color="secondary"
                                            onClick={() => onRemoveTransshipmentPort(index)}
                                        >
                                            <XCircle className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                        {t('form.fields.eta')}
                    </label>
                    <DatePicker
                        value={etaDate}
                        onChange={setEtaDate}
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between gap-4 mb-2">
                        <label className="block text-sm font-medium text-primary">
                            {t('form.fields.carriers', 'Carriers')}
                        </label>
                        <Button
                            type="button"
                            onClick={onAddCarrier}
                            size="sm"
                            color="secondary"
                            iconLeading={Plus}
                        >
                            {t('form.addNew', 'Add New')}
                        </Button>
                    </div>
                    {formData.carrierCompanyId ? (
                        <div className="flex items-center gap-2">
                            <div className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                                {formData.carrierName || 'Loading...'}
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                color="secondary"
                                onClick={() => {
                                    onInputChange('carrierCompanyId', '');
                                    onInputChange('carrierName', '');
                                    setCarrierSearch('');
                                }}
                            >
                                <XCircle className="size-4" />
                            </Button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Input
                                placeholder={t('form.fields.carriersPlaceholder', 'Search carrier...')}
                                value={effectiveCarrierSearch}
                                onChange={setCarrierSearch}
                                onFocus={() => setIsCarrierInputFocused(true)}
                                onBlur={() => {
                                    // Delay to allow click on dropdown item
                                    setTimeout(() => setIsCarrierInputFocused(false), 200);
                                }}
                                size="md"
                                aria-label={t('form.fields.carriers', 'Carriers')}
                            />
                            {isCarrierInputFocused && filteredCarrierOptions.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                                    {filteredCarrierOptions.map(carrier => (
                                        <button
                                            key={carrier.id}
                                            type="button"
                                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary"
                                            onMouseDown={(e) => {
                                                // Prevent input blur when clicking dropdown item
                                                e.preventDefault();
                                            }}
                                            onClick={() => {
                                                onInputChange('carrierCompanyId', carrier.id);
                                                onInputChange('carrierName', carrier.label);
                                                setCarrierSearch('');
                                                setIsCarrierInputFocused(false);
                                            }}
                                        >
                                            {carrier.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
